import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Layout from '../../components/Layout';
import ApiInspector from '../../components/ApiInspector';
import ComboFiltro from '../../components/ComboFiltro';
import { apiFetch } from '../../lib/api';
import { showToast } from '../../components/Toast';
import { useFiltroSocieta } from '../../hooks/useFiltroSocieta';
import { formatQuantity } from '../../utils/formatters';
import { API_DOCS } from './CruscottoMagazzinoPage.api';
import './CruscottoMagazzinoPage.css';

// ── Tipi ──────────────────────────────────────────────────────────────────────

type Origine = 'ORDINE_VENDITA' | 'DISTINTA';

type Copertura =
  | 'ALLESTITO'
  | 'DISPONIBILE'
  | 'CONTESO'
  | 'PARZIALE'
  | 'MANCANTE'
  | 'NON_GESTITO';

interface Alternativo {
  articoloId: number;
  codice: string;
  descrizione: string;
  giacenza: number;
}

interface RigaCruscotto {
  articoloId: number | null;
  codice: string | null;
  descrizione: string;
  tipologia: string | null;
  unitaMisura: string | null;
  origini: Origine[];
  richiesto: number;
  consegnato: number;
  daAllestire: number;
  inRda: number;
  giacenza: number | null;
  impegnatoAltriOrdini: number | null;
  disponibileNetto: number | null;
  copertura: Copertura;
  alternativi: Alternativo[];
}

interface GruppoOrdine {
  ordine: { id: number; numero: string; dataConferma: string | null };
  commessa: {
    id: number;
    numero: string;
    descrizione: string;
    clienteRagioneSociale: string;
  } | null;
  clienteRagioneSociale: string | null;
  daAllestireTotale: number;
  righeMancanti: number;
  righeContese: number;
  righe: RigaCruscotto[];
}

interface CommessaOption {
  id: number;
  numero: string;
  descrizione: string;
  clienteRagioneSociale: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// Estrae il messaggio dagli errori API ({error: string | {code,message}})
function estraiErrore(err: unknown, fallback: string): string {
  if (err && typeof err === 'object') {
    const payload = (err as { payload?: { error?: string | { code?: string; message?: string } } }).payload;
    const e = payload?.error;
    if (e && typeof e === 'object' && e.message) return e.message;
    if (typeof e === 'string' && e) return e;
    if (err instanceof Error && err.message && err.message !== '[object Object]') return err.message;
  }
  return fallback;
}

const COPERTURA_CFG: Record<Copertura, { label: string; cls: string; title?: string }> = {
  ALLESTITO:   { label: 'Allestito',    cls: 'bop-badge-success' },
  DISPONIBILE: { label: 'Disponibile',  cls: 'bop-badge-success' },
  CONTESO:     {
    label: 'Conteso',
    cls: 'bop-badge-warning',
    title: 'La giacenza basterebbe per questo ordine ma è impegnata anche da altri ordini',
  },
  PARZIALE:    { label: 'Parziale',     cls: 'bop-badge-warning' },
  MANCANTE:    { label: 'Mancante',     cls: 'bop-badge-danger' },
  NON_GESTITO: { label: 'No magazzino', cls: 'bop-badge-neutral' },
};

const N_COLONNE = 11;

// ─────────────────────────────────────────────────────────────────────────────
// CruscottoMagazzinoPage — pagina operativa del responsabile di magazzino:
// per ogni ordine confermato, la merce da allestire con giacenze, impegni
// e arrivi (RDA) in tempo reale. Solo lettura, nessuna azione di stato.
// ─────────────────────────────────────────────────────────────────────────────
export default function CruscottoMagazzinoPage() {
  const { societaId, listaSocieta, isDisabled } = useFiltroSocieta();

  // Stato locale società (solo SUPERADMIN con "Tutte" in topbar può modificarlo).
  // Il backend RICHIEDE sempre una società: senza selezione non si carica nulla.
  const [societaIdLocale, setSocietaIdLocale] = useState('');
  const societaIdEff = societaId || societaIdLocale;

  // Filtri
  const [commessaId, setCommessaId] = useState('');
  const [ordineId, setOrdineId] = useState('');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');

  // Dati
  const [gruppi, setGruppi] = useState<GruppoOrdine[]>([]);
  const [loading, setLoading] = useState(false);
  const [commesseOpts, setCommesseOpts] = useState<CommessaOption[]>([]);
  const [ordiniOpts, setOrdiniOpts] = useState<{ id: string; nome: string }[]>([]);

  // Accordion aperti (id ordine) + righe alternativi espanse (chiave "ordineId:idx")
  const [aperti, setAperti] = useState<Set<number>>(new Set());
  const [altEspansi, setAltEspansi] = useState<Set<string>>(new Set());

  // Debounce ricerca testo (400ms) — q viene passata al backend
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 400);
    return () => clearTimeout(t);
  }, [qInput]);

  // ── Carica cruscotto ────────────────────────────────────────────────────────
  const caricaCruscotto = useCallback(async () => {
    if (!societaIdEff) {
      setGruppi([]);
      return;
    }
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('societaId', societaIdEff);
      if (commessaId) params.set('commessaId', commessaId);
      if (ordineId) params.set('ordineId', ordineId);
      if (q) params.set('q', q);

      // @api GET /api/magazzino/cruscotto?societaId&commessaId&ordineId&q -> {"success":true,"data":[{"ordine":{"id":"number","numero":"string","dataConferma":"string|null"},"commessa":{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"},"clienteRagioneSociale":"string","daAllestireTotale":"number","righeMancanti":"number","righeContese":"number","righe":[{"articoloId":"number|null","codice":"string|null","descrizione":"string","tipologia":"string|null","unitaMisura":"string|null","origini":["ORDINE_VENDITA|DISTINTA"],"richiesto":"number","consegnato":"number","daAllestire":"number","inRda":"number","giacenza":"number|null","impegnatoAltriOrdini":"number|null","disponibileNetto":"number|null","copertura":"ALLESTITO|DISPONIBILE|CONTESO|PARZIALE|MANCANTE|NON_GESTITO","alternativi":[{"articoloId":"number","codice":"string","descrizione":"string","giacenza":"number"}]}]}]}
      const r = await apiFetch<{
        success: boolean;
        data?: GruppoOrdine[];
        error?: string | { code?: string; message?: string };
      }>(`/api/magazzino/cruscotto?${params.toString()}`);

      if (!r.success) {
        showToast(estraiErrore({ payload: r }, 'Errore nel caricamento del cruscotto'), 'error');
        setGruppi([]);
      } else {
        setGruppi(r.data || []);
      }
    } catch (err: unknown) {
      showToast(estraiErrore(err, 'Errore nel caricamento del cruscotto'), 'error');
      setGruppi([]);
    } finally {
      setLoading(false);
    }
  }, [societaIdEff, commessaId, ordineId, q]);

  useEffect(() => { caricaCruscotto(); }, [caricaCruscotto]);

  // Default: aperti i primi 3 ordini della risposta
  useEffect(() => {
    setAperti(new Set(gruppi.slice(0, 3).map(g => g.ordine.id)));
    setAltEspansi(new Set());
  }, [gruppi]);

  // ── Combo commesse (dipende dalla società effettiva) ────────────────────────
  useEffect(() => {
    if (!societaIdEff) {
      setCommesseOpts([]);
      return;
    }
    // @api GET /api/commesse?societaId={id} -> {"commesse":[{"id":"number","numero":"string","descrizione":"string","clienteRagioneSociale":"string"}]}
    apiFetch<{ commesse: CommessaOption[] }>(`/api/commesse?societaId=${societaIdEff}`)
      .then(r => setCommesseOpts(r.commesse || []))
      .catch(() => setCommesseOpts([]));
  }, [societaIdEff]);

  // Combo ordini: popolato dagli ordini presenti nella risposta.
  // Non si aggiorna mentre è attivo il filtro ordine (la risposta ne conterrebbe uno solo).
  useEffect(() => {
    if (ordineId) return;
    setOrdiniOpts(gruppi.map(g => ({
      id: String(g.ordine.id),
      nome: g.clienteRagioneSociale
        ? `${g.ordine.numero} — ${g.clienteRagioneSociale}`
        : g.ordine.numero,
    })));
  }, [gruppi, ordineId]);

  // Reset filtri dipendenti al cambio di società / commessa
  useEffect(() => { setCommessaId(''); setOrdineId(''); }, [societaIdEff]);
  useEffect(() => { setOrdineId(''); }, [commessaId]);

  const commesseComboItems = commesseOpts.map(c => ({
    id: String(c.id),
    nome: `${c.numero} — ${c.descrizione || ''}`.trim(),
  }));

  // ── KPI ─────────────────────────────────────────────────────────────────────
  const kpi = useMemo(() => {
    const ordiniDaAllestire = gruppi.filter(g => g.daAllestireTotale > 0).length;
    const righeMancanti = gruppi.reduce((s, g) => s + (g.righeMancanti || 0), 0);
    const righeContese = gruppi.reduce((s, g) => s + (g.righeContese || 0), 0);
    return { ordiniDaAllestire, righeMancanti, righeContese };
  }, [gruppi]);

  const totaleRighe = useMemo(
    () => gruppi.reduce((s, g) => s + g.righe.length, 0),
    [gruppi]
  );

  // ── Toggle ──────────────────────────────────────────────────────────────────
  const toggleOrdine = (id: number) => {
    setAperti(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleAlternativi = (chiave: string) => {
    setAltEspansi(prev => {
      const next = new Set(prev);
      if (next.has(chiave)) next.delete(chiave); else next.add(chiave);
      return next;
    });
  };

  const pulisciFiltri = () => {
    setCommessaId('');
    setOrdineId('');
    setQInput('');
    setQ('');
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <Layout>
      <div className="crmg-page">
        {/* Header */}
        <div className="bop-page-header">
          <div className="crmg-header-text">
            <h1 className="bop-page-title">Cruscotto Allestimento</h1>
            <p className="crmg-subtitle bop-text-muted">
              Per ogni ordine: merce da allestire, giacenze, impegni e arrivi — in tempo reale
            </p>
          </div>
        </div>

        {/* KPI */}
        <div className="crmg-kpi-grid">
          <div className="bop-card crmg-kpi">
            <div className="crmg-kpi__label">Ordini da allestire</div>
            <div className="crmg-kpi__value">{kpi.ordiniDaAllestire}</div>
          </div>
          <div className="bop-card crmg-kpi">
            <div className="crmg-kpi__label">Righe mancanti</div>
            <div className={`crmg-kpi__value ${kpi.righeMancanti > 0 ? 'crmg-kpi__value--danger' : ''}`}>
              {kpi.righeMancanti}
            </div>
          </div>
          <div className="bop-card crmg-kpi">
            <div className="crmg-kpi__label">Righe contese</div>
            <div className={`crmg-kpi__value ${kpi.righeContese > 0 ? 'crmg-kpi__value--warning' : ''}`}>
              {kpi.righeContese}
            </div>
          </div>
        </div>

        {/* Filtri */}
        <div className="bop-filter-bar crmg-filters-row">
          {!isDisabled && (
            <div className="bop-form-group">
              <label className="bop-label">Società</label>
              <select
                className="bop-select"
                value={societaIdLocale}
                onChange={e => setSocietaIdLocale(e.target.value)}
              >
                <option value="">— Seleziona —</option>
                {listaSocieta.map(s => (
                  <option key={s.id} value={String(s.id)}>{s.nome}</option>
                ))}
              </select>
            </div>
          )}

          <div className="bop-form-group crmg-combo-group">
            <label className="bop-label">Commessa</label>
            <ComboFiltro
              label="commessa"
              value={commessaId || null}
              onChange={id => setCommessaId(id || '')}
              items={commesseComboItems}
              disabled={!societaIdEff}
            />
          </div>

          <div className="bop-form-group crmg-combo-group">
            <label className="bop-label">Ordine</label>
            <ComboFiltro
              label="ordine"
              value={ordineId || null}
              onChange={id => setOrdineId(id || '')}
              items={ordiniOpts}
              disabled={!societaIdEff}
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">Cerca</label>
            <input
              className="bop-input crmg-search-input"
              value={qInput}
              onChange={e => setQInput(e.target.value)}
              placeholder="Codice, descrizione articolo..."
            />
          </div>

          <div className="bop-form-group">
            <label className="bop-label">&nbsp;</label>
            <button className="bop-btn bop-btn-secondary" onClick={pulisciFiltri}>
              Pulisci filtri
            </button>
          </div>
        </div>

        {/* Contenuto */}
        {!societaIdEff ? (
          <div className="bop-card">
            <div className="bop-empty-state">Seleziona una società</div>
          </div>
        ) : loading ? (
          <div className="bop-card">
            <div className="bop-empty-state">Caricamento cruscotto…</div>
          </div>
        ) : gruppi.length === 0 ? (
          <div className="bop-card">
            <div className="bop-empty-state">Nessun ordine da allestire per i filtri selezionati.</div>
          </div>
        ) : (
          <>
            <div className="crmg-accordion-list">
              {gruppi.map(g => {
                const aperto = aperti.has(g.ordine.id);
                return (
                  <div key={g.ordine.id} className="crmg-accordion">
                    <button
                      type="button"
                      className="crmg-accordion__header"
                      onClick={() => toggleOrdine(g.ordine.id)}
                      aria-expanded={aperto}
                    >
                      <span className="crmg-accordion__chevron">{aperto ? '▾' : '▸'}</span>
                      <span className="crmg-accordion__numero">Ordine {g.ordine.numero}</span>
                      {g.clienteRagioneSociale && (
                        <span className="crmg-accordion__cliente">{g.clienteRagioneSociale}</span>
                      )}
                      {g.commessa && (
                        <span className="crmg-accordion__commessa bop-text-muted">
                          {g.commessa.numero}{g.commessa.descrizione ? ` — ${g.commessa.descrizione}` : ''}
                        </span>
                      )}
                      <span className="crmg-accordion__spacer" />
                      {g.righeMancanti > 0 && (
                        <span className="bop-badge bop-badge-danger">{g.righeMancanti} mancanti</span>
                      )}
                      {g.righeContese > 0 && (
                        <span className="bop-badge bop-badge-warning">{g.righeContese} contese</span>
                      )}
                      {g.daAllestireTotale === 0 && (
                        <span className="bop-badge bop-badge-success">Tutto allestito</span>
                      )}
                      <span className="crmg-accordion__totale">
                        Da allestire: <strong>{formatQuantity(g.daAllestireTotale)}</strong>
                      </span>
                    </button>

                    {aperto && (
                      <div className="crmg-accordion__body">
                        <table className="bop-table crmg-tabella">
                          {/* Larghezze fisse: tutte le tabelle degli ordini restano allineate */}
                          <colgroup>
                            <col className="crmg-col--codice" />
                            <col className="crmg-col--descrizione" />
                            <col className="crmg-col--um" />
                            <col className="crmg-col--num" />
                            <col className="crmg-col--num" />
                            <col className="crmg-col--num" />
                            <col className="crmg-col--num" />
                            <col className="crmg-col--num" />
                            <col className="crmg-col--num" />
                            <col className="crmg-col--num" />
                            <col className="crmg-col--copertura" />
                          </colgroup>
                          <thead>
                            <tr>
                              <th>Codice</th>
                              <th>Descrizione</th>
                              <th>U.M.</th>
                              <th className="bop-text-right">Richiesto</th>
                              <th className="bop-text-right">Consegnato</th>
                              <th className="bop-text-right">Da allestire</th>
                              <th className="bop-text-right">Giacenza</th>
                              <th className="bop-text-right">Impegnato altri ordini</th>
                              <th className="bop-text-right">Disponibile netto</th>
                              <th className="bop-text-right">In arrivo (RDA)</th>
                              <th>Copertura</th>
                            </tr>
                          </thead>
                          <tbody>
                            {g.righe.map((r, idx) => {
                              const chiaveAlt = `${g.ordine.id}:${idx}`;
                              const cfg = COPERTURA_CFG[r.copertura] || COPERTURA_CFG.NON_GESTITO;
                              const critica = r.copertura === 'MANCANTE' || r.copertura === 'PARZIALE';
                              const haAlternativi =
                                r.tipologia === 'INTERCAMBIABILE' && (r.alternativi || []).length > 0;
                              const altAperto = altEspansi.has(chiaveAlt);
                              const alternativiOrdinati = haAlternativi
                                ? [...r.alternativi].sort((a, b) => (b.giacenza || 0) - (a.giacenza || 0))
                                : [];
                              return (
                                <React.Fragment key={chiaveAlt}>
                                  <tr className={critica ? 'crmg-riga--critica' : ''}>
                                    <td>
                                      {r.codice
                                        ? <span className="crmg-codice">{r.codice}</span>
                                        : <span className="bop-text-muted">—</span>}
                                    </td>
                                    <td>
                                      <span className="crmg-descrizione">{r.descrizione}</span>
                                      {haAlternativi && (
                                        <button
                                          type="button"
                                          className="crmg-alt-toggle"
                                          onClick={() => toggleAlternativi(chiaveAlt)}
                                          aria-expanded={altAperto}
                                        >
                                          {altAperto ? '▾' : '▸'} {alternativiOrdinati.length} alternativi
                                        </button>
                                      )}
                                    </td>
                                    <td>{r.unitaMisura || <span className="bop-text-muted">—</span>}</td>
                                    <td className="bop-text-right">{formatQuantity(r.richiesto)}</td>
                                    <td className="bop-text-right">{formatQuantity(r.consegnato)}</td>
                                    <td className="bop-text-right crmg-td-da-allestire">
                                      {formatQuantity(r.daAllestire)}
                                    </td>
                                    <td className="bop-text-right">
                                      {r.giacenza != null
                                        ? formatQuantity(r.giacenza)
                                        : <span className="bop-text-muted">—</span>}
                                    </td>
                                    <td className="bop-text-right">
                                      {r.impegnatoAltriOrdini != null
                                        ? formatQuantity(r.impegnatoAltriOrdini)
                                        : <span className="bop-text-muted">—</span>}
                                    </td>
                                    <td className={`bop-text-right ${r.disponibileNetto != null && r.disponibileNetto < 0 ? 'bop-text-danger' : ''}`}>
                                      {r.disponibileNetto != null
                                        ? formatQuantity(r.disponibileNetto)
                                        : <span className="bop-text-muted">—</span>}
                                    </td>
                                    <td className="bop-text-right">
                                      {r.inRda > 0
                                        ? formatQuantity(r.inRda)
                                        : <span className="bop-text-muted">—</span>}
                                    </td>
                                    <td>
                                      <span className={`bop-badge ${cfg.cls}`} title={cfg.title}>
                                        {cfg.label}
                                      </span>
                                    </td>
                                  </tr>
                                  {haAlternativi && altAperto && (
                                    <tr className="crmg-row-alt">
                                      <td colSpan={N_COLONNE}>
                                        <div className="crmg-alt">
                                          <div className="crmg-alt__title">Alternativi disponibili</div>
                                          <ul className="crmg-alt__list">
                                            {alternativiOrdinati.map(a => (
                                              <li key={a.articoloId} className="crmg-alt__item">
                                                <span className="crmg-alt__codice">{a.codice}</span>
                                                <span className="crmg-alt__desc">{a.descrizione}</span>
                                                <span className={`bop-badge ${a.giacenza > 0 ? 'bop-badge-success' : 'bop-badge-neutral'}`}>
                                                  {formatQuantity(a.giacenza)}
                                                </span>
                                              </li>
                                            ))}
                                          </ul>
                                        </div>
                                      </td>
                                    </tr>
                                  )}
                                </React.Fragment>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            <div className="crmg-record-count bop-text-muted">
              {gruppi.length} ordini — {totaleRighe} righe articolo
            </div>
          </>
        )}

        <ApiInspector docs={API_DOCS} />
      </div>
    </Layout>
  );
}
